import React, { useEffect, useState } from "react";
import { ArrowRight, BookOpen, LayoutDashboard, LineChart, Award, Palette, Globe } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Mandala from "@/components/Mandala";
import TrainerApplicationForm from "@/components/trainer/TrainerApplicationForm";
// Phase artwork (cropped from Holly's Legacy Path image), rendered wherever the phase icon is used.
const phaseImg = (slug, label) => {
  const Img = ({ className = "" }) => (
    <img src={`/legacy/${slug}.webp`} alt={`${label} — Aviana Legacy Path`} loading="lazy"
      className={`${className} !w-full !h-full rounded-full object-cover`} />
  );
  return Img;
};
const SeedIcon = phaseImg("seed", "Seed");
const RootIcon = phaseImg("root", "Root");
const EmergeIcon = phaseImg("emerge", "Emerge");
const BloomIcon = phaseImg("bloom", "Bloom");
const CultivateIcon = phaseImg("cultivate", "Cultivate");

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const FALLBACK = {
  hero: `${BACKEND}/api/files/04fa4ba1-8d12-4e6f-b75e-3014a19c0eaf`,
  "partners-photo": `${BACKEND}/api/files/153e6315-dd45-46f1-981f-3c89c82544ed`,
};

const PHASES = [
  { n: 1, name: "Seed", Icon: SeedIcon, subtitle: "Your foundation.",
    body: "Every journey begins somewhere. Having completed your Aviana Aerial Yoga Teacher Training, you have planted the seed of your own practice and begun embodying the heart of the Aviana method.",
    milestone: "Become an Aviana Certified Instructor." },
  { n: 2, name: "Root", Icon: RootIcon, subtitle: "Deepening your connection.",
    body: "You deepen your understanding of Aviana by auditing or apprenticing during a training. This is an opportunity to experience the curriculum from a trainer's perspective, observe the flow of a training, and begin envisioning yourself in that role.",
    milestone: "Complete an Aviana training audit or apprenticeship." },
  { n: 3, name: "Emerge", Icon: EmergeIcon, subtitle: "Stepping into your own light.",
    body: "This is where you begin. You lead your first Aviana Teacher Training, bringing your own personality, experience, and teaching gifts to the process. Holly is here as a resource while you find your rhythm, with the first 10 instructor certifications reviewed together to help you build confidence and consistency. You don't have to know everything from the beginning. You grow into the role by doing the work, learning through experience, and knowing support is available.",
    milestone: "Lead your first trainings with support as you establish yourself." },
  { n: 4, name: "Bloom", Icon: BloomIcon, subtitle: "Confidence, autonomy, and leadership.",
    body: "You have found your footing and grown into your role as an Aviana Teacher Trainer. With experience behind you, you are ready to lead trainings and certify instructors independently, trusting your knowledge, your voice, and your ability to uphold the integrity of the Aviana method. This is your opportunity to shine in your own way while remaining connected to the wider Aviana community.",
    milestone: "Become an independently authorized Aviana Teacher Trainer." },
  { n: 5, name: "Cultivate", Icon: CultivateIcon, subtitle: "Co-creating the future of Aviana.",
    body: "The journey comes full circle as you help nurture what comes next. As a trusted creative partner, you collaborate with Holly to explore new offerings, contribute ideas, and develop future Aviana programs. Your experience and perspective become part of the ongoing evolution of the work.",
    milestone: "Step into creative partnership and help shape the future of Aviana." },
];

const RECEIVE = [
  { Icon: BookOpen, title: "The licensed Aviana curriculum", body: "The full method, entrusted to you — the sequences, philosophy, and sacred structure refined over more than a decade of teaching." },
  { Icon: LayoutDashboard, title: "An online student portal", body: "A beautiful home for your students between sessions, with lessons, quizzes, and resources waiting for them whenever they need them." },
  { Icon: LineChart, title: "Student progress tracking", body: "Gently follow each student's journey — teaching hours, evaluations, and submissions — all held in one place." },
  { Icon: Award, title: "Certification authority", body: "The honor of certifying new Aviana instructors and welcoming them into the lineage with your own blessing." },
  { Icon: Palette, title: "Aviana brand assets", body: "Imagery, language, and design so you can share Aviana with the same beauty and care it was born with." },
  { Icon: Globe, title: "A place on the Aviana website", body: "Your trainings featured on avianayoga.com, so the women who are searching can find their way to you." },
];

const STANDARDS = [
  "Complete the Aviana Aerial Yoga Teacher Training.",
  "Complete an apprenticeship — by auditing and assisting a training, or through a private mentorship with Holly.",
  "Maintain current liability insurance, submitted annually.",
  "Complete the Mythical Goddess Tarot course by Katherine Skaggs.",
  "Follow Aviana teaching standards and certification procedures.",
];

function Hero({ img }) {
  return (
    <section className="relative min-h-[88vh] flex items-end pt-40 pb-20 md:pb-28 overflow-hidden text-aviana-cream" data-testid="trainer-hero">
      <div className="absolute inset-0">
        {img && <img src={img} alt="An Aviana aerial yoga teacher training circle" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep via-aviana-plumDeep/70 to-aviana-ink/30" />
      </div>
      <div className="container-aviana relative max-w-4xl">
        <p className="overline text-aviana-goldSoft mb-6 tracking-[0.32em]">Become an Aviana Teacher Trainer</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98]" data-testid="trainer-headline">
          Carry the light <em className="italic font-serif gold-text">forward.</em>
        </h1>
        <p className="mt-8 font-serif italic text-[19px] md:text-[23px] leading-[1.55] text-aviana-cream/90 max-w-2xl">
          Aviana was never meant to live in one woman alone. This is an invitation to receive the torch — to weave the Aviana Method into your own community as part of a living lineage of women who carry it with integrity and heart.
          If you feel the stirring, you are already on the path.
        </p>
        <div className="mt-11 flex flex-wrap items-center gap-6">
          <a href="#legacy-path" className="btn-gold !px-8 !py-4 !text-[13px]" data-testid="trainer-hero-path">Walk the Legacy Path <ArrowRight size={14} /></a>
          <a href="#apply" className="text-aviana-goldSoft hover:text-aviana-cream text-[13px] tracking-[0.18em] uppercase border-b border-aviana-goldSoft/40 pb-1 transition-colors" data-testid="trainer-hero-apply">Apply to become a trainer →</a>
        </div>
      </div>
    </section>
  );
}

function PathOverview() {
  return (
    <div className="relative hidden md:grid grid-cols-5 gap-4 mb-24" data-testid="legacy-path-overview">
      <div className="absolute top-14 left-[10%] right-[10%] h-px bg-gradient-to-r from-aviana-gold/20 via-aviana-gold to-aviana-gold/20" />
      {PHASES.map(({ n, name, Icon }) => (
        <a key={n} href={`#phase-${name.toLowerCase()}`} className="relative flex flex-col items-center text-center group">
          <span className="w-28 h-28 rounded-full overflow-hidden flex items-center justify-center shadow-[0_14px_34px_-14px_rgba(90,42,91,0.45)] group-hover:-translate-y-1 transition-transform duration-300">
            <Icon className="" />
          </span>
          <span className="mt-4 text-[10px] tracking-[0.28em] uppercase text-aviana-muted">Phase {n}</span>
          <span className="font-display text-2xl text-aviana-ink">{name}</span>
        </a>
      ))}
    </div>
  );
}

function Phase({ p, last }) {
  return (
    <li id={`phase-${p.name.toLowerCase()}`} className="relative grid grid-cols-[72px_1fr] md:grid-cols-[120px_1fr] gap-6 md:gap-10 scroll-mt-28" data-testid={`legacy-phase-${p.name.toLowerCase()}`}>
      <div className="relative flex flex-col items-center">
        <span className="relative z-10 w-[72px] h-[72px] md:w-28 md:h-28 rounded-full overflow-hidden flex items-center justify-center shadow-[0_14px_34px_-14px_rgba(90,42,91,0.45)]">
          <p.Icon className="" />
        </span>
        {!last && <span className="flex-1 w-px bg-gradient-to-b from-aviana-gold/70 to-aviana-gold/10 mt-2" />}
      </div>
      <div className="pb-16 md:pb-20">
        <p className="text-[11px] tracking-[0.3em] uppercase text-aviana-gold">Phase {p.n}</p>
        <h3 className="font-display text-4xl md:text-5xl text-aviana-ink mt-1 tracking-[0.04em] uppercase">{p.name}</h3>
        <p className="font-serif italic text-[20px] md:text-[22px] text-aviana-plum mt-2">{p.subtitle}</p>
        <p className="mt-5 text-aviana-muted leading-[1.9] text-[16px] md:text-[17px] max-w-2xl">{p.body}</p>
        <p className="mt-6 flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 bg-aviana-cream border border-aviana-gold/40 rounded-2xl px-5 py-3.5 max-w-2xl w-fit" data-testid={`legacy-milestone-${p.name.toLowerCase()}`}>
          <span className="text-[10px] tracking-[0.24em] uppercase text-aviana-plum whitespace-nowrap pt-1">Your milestone</span>
          <span className="text-[15px] text-aviana-ink">{p.milestone}</span>
        </p>
      </div>
    </li>
  );
}

function LegacyPath() {
  return (
    <section id="legacy-path" className="py-28 md:py-36 bg-aviana-paper relative overflow-hidden scroll-mt-20" data-testid="legacy-path">
      <div className="absolute -left-48 top-16 opacity-[0.07] pointer-events-none"><Mandala size={520} variant="lotus" className="text-aviana-gold" /></div>
      <div className="container-aviana relative max-w-5xl">
        <div className="max-w-2xl mb-20">
          <p className="overline-plum mb-4">The journey</p>
          <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">The Aviana <em className="italic font-serif gold-text">Legacy Path.</em></h2>
          <p className="mt-6 font-serif italic text-[20px] text-aviana-inkSoft">A journey of growth, leadership, and creative partnership.</p>
        </div>
        <PathOverview />
        <ol className="max-w-4xl">
          {PHASES.map((p, i) => <Phase key={p.n} p={p} last={i === PHASES.length - 1} />)}
        </ol>
      </div>
    </section>
  );
}

function Receive({ img }) {
  return (
    <section className="py-28 md:py-36 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden" data-testid="partners-receive">
      <div className="container-aviana relative grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <p className="overline text-aviana-goldSoft mb-4 tracking-[0.3em]">What partners receive</p>
          <h2 className="font-display text-5xl md:text-6xl leading-[1.02]">Everything you need to <em className="italic font-serif text-aviana-goldSoft">hold the work well.</em></h2>
          <p className="mt-6 text-aviana-cream/75 leading-[1.85] text-[17px]">You won't carry this alone. Every partner is supported with the tools, the structure and the sisterhood to share Aviana beautifully — so you can pour your energy into your students.</p>
          {img && <div className="mt-10 rounded-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 aspect-[4/3]"><img src={img} alt="An Aviana teacher training in session" loading="lazy" className="w-full h-full object-cover" /></div>}
        </div>
        <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {RECEIVE.map(({ Icon, title, body }) => (
            <li key={title} className="rounded-[1.5rem] border border-aviana-gold/25 bg-white/[0.04] backdrop-blur-sm p-7 hover:border-aviana-gold/60 transition-colors" data-testid={`receive-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={22} strokeWidth={1.4} className="text-aviana-goldSoft" />
              <h3 className="font-display text-2xl mt-5 leading-tight">{title}</h3>
              <p className="mt-3 text-aviana-cream/70 leading-[1.8] text-[15px]">{body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Stewardship() {
  return (
    <section className="py-28 md:py-32 bg-aviana-cream" data-testid="standards-of-stewardship">
      <div className="container-aviana max-w-4xl">
        <p className="overline-plum mb-4">Partner requirements</p>
        <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">Standards of <em className="italic font-serif gold-text">Stewardship.</em></h2>
        <p className="mt-6 text-aviana-muted leading-[1.85] text-[17px] max-w-2xl">These standards protect the heart of the work — and every student who trusts it.</p>
        <ol className="mt-12 divide-y divide-aviana-border">
          {STANDARDS.map((s, i) => (
            <li key={s} className="py-6 flex gap-6 items-baseline" data-testid={`standard-${i + 1}`}>
              <span className="font-display text-3xl gold-text w-10 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-[17px] text-aviana-inkSoft leading-[1.7]">{s}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Apply() {
  return (
    <section id="apply" className="py-28 md:py-36 bg-aviana-paper scroll-mt-20" data-testid="trainer-apply">
      <div className="container-aviana max-w-3xl">
        <div className="text-center mb-14">
          <span className="ornament-flower mx-auto block" />
          <p className="overline-plum mt-8 mb-4">Apply</p>
          <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">Answer <em className="italic font-serif gold-text">the call.</em></h2>
          <p className="mt-6 text-aviana-muted leading-[1.85] text-[17px] max-w-xl mx-auto">Share a little of who you are and where you'd love to carry this work. Holly reads every application personally.</p>
        </div>
        <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-7 md:p-12 shadow-[0_30px_60px_-40px_rgba(61,27,64,0.35)]">
          <TrainerApplicationForm />
        </div>
      </div>
    </section>
  );
}

export default function BecomeTrainer() {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    api.get("/page-images/become-trainer").then(({ data }) => setSlots(data.slots || {})).catch(() => setSlots({}));
  }, []);
  const pick = (k) => {
    if (!slots) return null;
    const u = slots[k]?.[0];
    return u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : FALLBACK[k];
  };
  return (
    <div data-testid="become-trainer-page">
      <SEO
        title="Become an Aerial Yoga Teacher Trainer · The Aviana Legacy Path"
        description="Carry the Aviana Method into your community. Experienced Aviana instructors can grow into authorized Aviana Aerial Yoga Teacher Trainers through the five-phase Aviana Legacy Path."
        path="/become-an-aerial-yoga-teacher-trainer"
        keywords="become an aerial yoga teacher trainer, aerial yoga trainer license, Aviana teacher trainer, aerial yoga lineage"
      />
      <Hero img={pick("hero")} />
      <LegacyPath />
      <Receive img={pick("partners-photo")} />
      <Stewardship />
      <Apply />
    </div>
  );
}
